import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { DatePicker } from './ui/DatePicker';
import { ArrowLeft, Save } from 'lucide-react';
interface TaxOnFeeProfileFormProps {
  onBack: () => void;
  onSave: () => void;
}
export function TaxOnFeeProfileForm({
  onBack,
  onSave
}: TaxOnFeeProfileFormProps) {
  // Generate a random UUID for display
  const [profileId] = useState(`TOF-${Math.random().toString(36).substr(2, 9).toUpperCase()}`);
  // Form State
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    effectiveDate: '',
    tax: '',
    taxType: 'Percentage',
    taxValue: '',
    fixedAmount: '',
    filerStatus: 'Both',
    glAccount: ''
  });
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <div className="flex flex-col h-full bg-gray-50" data-id="element-1056">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-1057">
        <div className="flex items-center" data-id="element-1058">
          <Button variant="ghost" onClick={onBack} className="mr-4" data-id="element-1059">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-1060" />
            Back to List
          </Button>
          <div data-id="element-1061">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-1062">
              New Tax on Fee Profile
            </h1>
            <p className="text-sm text-gray-500" data-id="element-1063">
              Configure tax rules for fee charges
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-1064">
          <Button variant="secondary" onClick={onBack} data-id="element-1065">
            Cancel
          </Button>
          <Button onClick={onSave} data-id="element-1066">
            <Save className="h-4 w-4 mr-2" data-id="element-1067" />
            Save Profile
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full space-y-8" data-id="element-1068">
        {/* Section 1: Profile Header */}
        <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1069">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100" data-id="element-1070">
            Profile Header
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1071">
            <div className="space-y-1.5" data-id="element-1072">
              <label className="text-sm font-medium text-gray-700" data-id="element-1073">
                Product Tax ID
              </label>
              <div className="flex h-10 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" data-id="element-1074">
                {profileId}
              </div>
              <p className="text-xs text-gray-500" data-id="element-1075">
                Auto-generated unique identifier
              </p>
            </div>

            <Input label="Product Tax Code" value={formData.code} onChange={e => handleChange('code', e.target.value)} placeholder="e.g., TOF-SVC-001" helperText="Used for integration routing" required data-id="element-1076" />

            <Input label="Product Tax Name" value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder="e.g., Service Tax on Processing Fee" helperText="Display name for product setup selection" required data-id="element-1077" />

            <DatePicker label="Effective Date" value={formData.effectiveDate} onChange={e => handleChange('effectiveDate', e.target.value)} helperText="Date when this tax profile becomes applicable" required data-id="element-1078" />

            <div className="md:col-span-2" data-id="element-1079">
              <Select label="Tax" value={formData.tax} onChange={e => handleChange('tax', e.target.value)} options={[{
              value: 'Service Tax',
              label: 'Service Tax'
            }, {
              value: 'Withholding Tax',
              label: 'Withholding Tax'
            }, {
              value: 'FED Tax',
              label: 'FED Tax'
            }, {
              value: 'VAT Tax',
              label: 'VAT Tax'
            }]} helperText="Defines the type of tax applied" required data-id="element-1080" />
            </div>
          </div>
        </section>

        {/* Section 2: Tax Detail */}
        <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-6 duration-500 delay-100" data-id="element-1081">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100" data-id="element-1082">
            Tax Detail
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1083">
            <Select label="Tax Type" value={formData.taxType} onChange={e => handleChange('taxType', e.target.value)} options={[{
            value: 'Percentage',
            label: 'Percentage - % of base amount'
          }, {
            value: 'Fixed',
            label: 'Fixed - Flat fee'
          }, {
            value: 'Fixed + Percentage',
            label: 'Fixed + Percentage - Combined'
          }]} helperText="Defines how tax is calculated" required data-id="element-1084" />

            <Input label={formData.taxType === 'Fixed' ? 'Fixed Amount' : 'Percentage (%)'} type="number" value={formData.taxValue} onChange={e => handleChange('taxValue', e.target.value)} placeholder={formData.taxType === 'Fixed' ? 'e.g., 100' : 'e.g., 18'} helperText={formData.taxType === 'Fixed' ? 'Fixed amount in PKR' : 'Percentage value (e.g., 18 for 18%)'} required data-id="element-1085" />

            {formData.taxType === 'Fixed + Percentage' && <Input label="Fixed Amount" type="number" value={formData.fixedAmount} onChange={e => handleChange('fixedAmount', e.target.value)} placeholder="e.g., 100" helperText="Additional fixed amount in PKR" required data-id="element-1086" />}

            <Select label="Filer / Non-Filer" value={formData.filerStatus} onChange={e => handleChange('filerStatus', e.target.value)} options={[{
            value: 'Filer',
            label: 'Filer - Tax filers only'
          }, {
            value: 'Non-Filer',
            label: 'Non-Filer - Non-filers only (higher rate)'
          }, {
            value: 'Both',
            label: 'Both - Universal application'
          }]} helperText="Pakistan-specific tax rules based on filing status" required data-id="element-1087" />

            <Select label="GL Account" value={formData.glAccount} onChange={e => handleChange('glAccount', e.target.value)} options={[{
            value: '2100',
            label: '2100 - Tax Payable - Service Tax'
          }, {
            value: '2200',
            label: '2200 - Tax Payable - FED'
          }, {
            value: '2300',
            label: '2300 - Tax Payable - VAT'
          }, {
            value: '2400',
            label: '2400 - Tax Payable - Withholding'
          }]} helperText="For posting tax entries in the general ledger" required data-id="element-1088" />
          </div>
        </section>
      </div>
    </div>;
}